import { calcApiBase } from '../calcConfig';

export class ApiError extends Error {
  constructor(message, { status, code, body } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status ?? 0;
    this.code = code ?? '';
    this.body = body;
  }
}

let config = {
  getToken: null,
  onUnauthorized: null,
  onProfileIncomplete: null,
};

export function configureApi({ getToken, onUnauthorized, onProfileIncomplete } = {}) {
  config = {
    getToken: getToken ?? config.getToken,
    onUnauthorized: onUnauthorized ?? config.onUnauthorized,
    onProfileIncomplete: onProfileIncomplete ?? config.onProfileIncomplete,
  };
}

export function _resetApiConfigForTests() {
  config = { getToken: null, onUnauthorized: null, onProfileIncomplete: null };
}

async function buildHeaders(extra) {
  const headers = { Accept: 'application/json', ...(extra || {}) };
  if (typeof config.getToken === 'function') {
    try {
      const token = await config.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch {
      // Fall through unauthenticated — the response handler will surface 401.
    }
  }
  return headers;
}

function joinUrl(base, path) {
  if (!path.startsWith('/')) path = `/${path}`;
  return `${base}${path}`;
}

async function readErrorBody(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

async function handleResponse(res, { expectBlob } = {}) {
  if (res.status === 401) {
    if (typeof config.onUnauthorized === 'function') config.onUnauthorized();
    const body = await readErrorBody(res);
    throw new ApiError('unauthorized', { status: 401, code: 'unauthorized', body });
  }
  if (res.status === 403) {
    const body = await readErrorBody(res);
    const serverCode = body && body.code;
    const serverError = body && body.error;
    if (serverCode === 'profile_incomplete' || serverError === 'profile_completion_required') {
      if (typeof config.onProfileIncomplete === 'function') config.onProfileIncomplete();
      throw new ApiError('profile_completion_required', { status: 403, code: 'profile_completion_required', body });
    }
    throw new ApiError('forbidden', { status: 403, code: serverCode || serverError || 'forbidden', body });
  }
  if (!res.ok) {
    const body = await readErrorBody(res);
    const code = (body && (body.code || body.error)) || `http_${res.status}`;
    throw new ApiError(`request failed (${res.status})`, { status: res.status, code, body });
  }
  if (expectBlob) return res.blob();
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  return JSON.parse(text);
}

async function request(method, path, { body, headers, expectBlob } = {}) {
  const url = joinUrl(calcApiBase(), path);
  const init = { method, headers: await buildHeaders(headers) };
  if (body !== undefined && body !== null) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const res = await fetch(url, init);
  return handleResponse(res, { expectBlob });
}

export const apiGet = (path, opts) => request('GET', path, opts);
export const apiPost = (path, body, opts) => request('POST', path, { ...(opts || {}), body });
export const apiPatch = (path, body, opts) => request('PATCH', path, { ...(opts || {}), body });
export const apiDelete = (path, opts) => request('DELETE', path, opts);
export const apiGetBlob = (path, opts) => request('GET', path, { ...(opts || {}), expectBlob: true });
export const apiPostBlob = (path, body, opts) => request('POST', path, { ...(opts || {}), body, expectBlob: true });
